import { useEffect, useRef, useState } from "react";
interface UsePageScrollOptions {
  pageCount: number;
  interval?: number;
  distance?: number;
  delay?: number;
  lastPageOffset?: number; // 最后一页向上偏移量
}

export function usePageScroll(options: UsePageScrollOptions) {
  const { pageCount, interval = 200, distance = 100, delay = 400, lastPageOffset = 0 } = options;
  const [topHeight, setTopHeight] = useState(0);
  const clientHeightRef = useRef(0);
  const currentIdxRef = useRef(0);

  const current = useRef({
    isScroll: true,
    scrollTime: 0 as number | null,
    timer: null as ReturnType<typeof setTimeout> | null,
  });

  function throttle<T extends (...args: any[]) => void>(func: T, limit: number) {
    let lastTime = 0;
    return function (this: any, ...args: Parameters<T>) {
      const now = Date.now();
      if (now - lastTime >= limit) {
        func.apply(this, args);
        lastTime = now;
      }
    };
  }

  const updateTopHeight = () => {
    if (currentIdxRef.current === pageCount - 1 && lastPageOffset > 0) {
      setTopHeight((currentIdxRef.current - 1) * clientHeightRef.current + lastPageOffset);
    } else {
      setTopHeight(currentIdxRef.current * clientHeightRef.current);
    }
  };

  const handleScroll = throttle((delta: number) => {
    if (delta > 0 && currentIdxRef.current < pageCount - 1) {
      currentIdxRef.current++;
    } else if (delta < 0 && currentIdxRef.current > 0) {
      currentIdxRef.current--;
    } else {
      return;
    }
    updateTopHeight();
  }, 100);

  useEffect(() => {
    clientHeightRef.current = document.documentElement.clientHeight;
    updateTopHeight();

    const onWheel = (e: WheelEvent) => {
      const y = e.deltaY;
      const time = Date.now();

      if (current.current.scrollTime) {
        if (time - current.current.scrollTime > interval && Math.abs(y) >= distance) {
          handleScroll(y);
          if (current.current.timer) clearTimeout(current.current.timer);
          current.current.isScroll = false;
          current.current.scrollTime = time;
        }
      }
      if (current.current.isScroll) {
        handleScroll(y);
        current.current.scrollTime = Date.now();
        current.current.isScroll = false;
      }
      if (current.current.timer) clearTimeout(current.current.timer);
      current.current.timer = setTimeout(() => {
        current.current.isScroll = true;
      }, delay);
    };

    let startY = 0;
    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e: TouchEvent) => {
      const endY = e.changedTouches[0].clientY;
      const dist = startY - endY;
      if (Math.abs(dist) < 50) return;
      handleScroll(dist);
    };

    const resizeHandler = () => {
      clientHeightRef.current = document.documentElement.clientHeight;
      updateTopHeight();
    };

    window.addEventListener("wheel", onWheel);
    window.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  return { topHeight };
}
